import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Button from "@mui/material/Button";

import { useNitroStore } from "../../store/Store";
import { useNitroCAStore } from "../../store/CompetitiveAnalysisStore";
import Occupants from "../../components/occupants";
import Logistics from "../../components/logistics";
import ILocation, { IOccupant } from "../../types/ILocation";
import FacilityForm from "./components/FacilityForm";

import LocationSchema from "./schemas/LocationSchema";
import { OccupantsArraySchema } from "./schemas/OccupantSchema";
import NitroService from "../../utils/nitro.service";
import {
  createBlankLocation,
  createBlankOccupant,
} from "../../components/occupants/components/BlankOccupant";
import LoadingIcon from "../../components/Loading";
import NitroModal from "../../components/Modal";
import PinSelectConfirmation from "../../components/PinSelectConfirmation";
import ZIndex from "../../styles/zindex";

import CloseIcon from "../../components/CloseIcon";
import useEscapeKeyExit from "../../hooks/useEscapeKeyExit";

import "./index.css";
import { convertModeParams } from "../multiLocationQuotePanel/components/multiLocationQuoteUtils";
import { toModes } from "../../utils/utils";
import { hasRequiredRailStationAndCarriers } from "../../utils/logisticsValidation";

const AddLocationPanel = () => {
  const [
    selectingPinLocation,
    setSelectingPinLocation,
    addLocation,
    pinLocation,
    setPinLocation,
    savedNotification,
    failedNotification,
    currentFilters,
    pendingRailStationUpdate,
    setPendingRailStationUpdate,
    setPendingWhereFilterUpdate,
    showAddLocationPanel,
    setShowAddLocationPanel,
    setMapType,
  ] = useNitroStore((state) => [
    state.selectingPinLocation,
    state.setSelectingPinLocation,
    state.addLocation,
    state.pinLocation,
    state.setPinLocation,
    state.savedNotification,
    state.failedNotification,
    state.currentFilters,
    state.pendingRailStationUpdate,
    state.setPendingRailStationUpdate,
    state.setPendingWhereFilterUpdate,
    state.showAddLocationPanel,
    state.setShowAddLocationPanel,
    state.setMapType,
  ]);
  const triggerRefetchQuotes = useNitroCAStore(
    (state) => state.triggerRefetchQuotes,
  );

  const [logisticsDisabled, setLogisticsDisabled] = useState(true);
  const [occupantsDisabled, setOccupantsDisabled] = useState(true);

  const [location, setLocation] = useState<ILocation | undefined>(
    createBlankLocation(),
  );

  useEffect(() => {
    if (
      !pendingRailStationUpdate ||
      pendingRailStationUpdate.locationId !== location?.id ||
      pendingRailStationUpdate.targetRowId
    )
      return;
    setLocation((prev) => ({
      ...(prev || createBlankLocation()),
      railStation: pendingRailStationUpdate.stationName,
      stationState: pendingRailStationUpdate.stationState,
      carriers: pendingRailStationUpdate.carriers,
      scrsId: pendingRailStationUpdate.scrsId,
    }));
    setPendingRailStationUpdate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRailStationUpdate]);

  const [occupantsAreValid, setOccupantsAreValid] = useState(false);

  // ################################################################################################################################
  // ## VALIDATION ##################################################################################################################
  // ################################################################################################################################
  const [isValid, setIsValid] = useState(false);
  useEffect(() => {
    if (!location) {
      setIsValid(false);
      return;
    }

    const latitude = Number(location.coordinates[0]);
    const longitude = Number(location.coordinates[1]);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      setIsValid(false);
      return;
    }

    if (latitude > 90 || latitude < -90) {
      setIsValid(false);
      return;
    }

    if (longitude > 180 || longitude < -180) {
      setIsValid(false);
      return;
    }

    const normalizedLocation = {
      ...location,
      name: (location.name || "").trim(),
      owner: (location.owner || "").trim(),
      address: (location.address || "").trim(),
      city: (location.city || "").trim(),
      state: (location.state || "").trim(),
      zipCode: (location.zipCode || "").trim(),
      country: (location.country || "").trim(),
      uom: (location.uom || "").trim(),
      coordinates: [latitude, longitude] as [number, number],
    };

    setIsValid(LocationSchema.isValidSync(normalizedLocation));
    setOccupantsAreValid(OccupantsArraySchema.isValidSync(location.occupants));
  }, [location]);

  const checkLocation = useCallback(async () => {
    if (isValid) {
      setLogisticsDisabled(false);
      setOccupantsDisabled(false);
    } else {
      setLogisticsDisabled(true);
      setOccupantsDisabled(true);
    }
  }, [isValid]);

  useEffect(() => {
    checkLocation();
  }, [isValid, checkLocation]);

  // initial form data values setup when panel opens
  useEffect(() => {
    if (!showAddLocationPanel) {
      setPinLocation(undefined);
    }

    const blankLocation = createBlankLocation();

    if (currentFilters.party) {
      blankLocation.owner = currentFilters.party;
      if (blankLocation.occupants[0]) {
        blankLocation.occupants[0].name = currentFilters.party;
      }
    }

    setLocation(blankLocation);
  }, [currentFilters.party, showAddLocationPanel, setPinLocation]);

  const closeAddLocationPanel = () => {
    setPinLocation(undefined);
    setSelectingPinLocation(false);
    setShowAddLocationPanel(false);
  };

  const [isSaving, setIsSaving] = useState(false);
  const saveLocation = async () => {
    if (!location) return;
    setIsSaving(true);
    try {
      // consolidate createLocation, updateRailStation and updateLocationModes into createLocation
      await NitroService.createLocation({
        id: location.id,
        name: location.name,
        owner: location.owner,
        address: location.address,
        city: location.city,
        state: location.state,
        zipCode: location.zipCode,
        country: location.country,
        coordinates: [
          Number(location.coordinates[0]),
          Number(location.coordinates[1]),
        ],
        uom: location.uom,
        modes: convertModeParams(
          toModes(location.inboundModes, location.outboundModes),
        ),
        equipmentTypes: location.equipmentTypes ?? [],
        threeLegsAllowed: location.threeLegsAllowed ?? false,
        railStations: (
          location.railStations?.length
            ? location.railStations
            : [
                {
                  scrsId: location.scrsId,
                  stationState: location.stationState,
                  carriers: location.carriers,
                },
              ]
        ).map((station) => ({
          scrsId: station.scrsId || undefined,
          stationState: station.scrsId ? "" : station.stationState,
          carriers: station.carriers?.map((carrier) => carrier.id) ?? [],
        })),
        occupants: location.occupants.map((occupant) => ({
          ...occupant,
          states: !occupant.types.includes("Customer") ? occupant.states : [],
          types: occupant.types.includes("Competitor")
            ? ["Competitor", "Customer"]
            : occupant.types,
        })),
      });
      const result = await NitroService.getPins(
        {
          locationId: location.id,
          partyTypes: "Koch,Competitor,Customer",
          shipDate: currentFilters.month,
        },
        0,
        0,
      );

      if (result && result.length > 0) {
        addLocation(result[0]);
      }
      closeAddLocationPanel();
      savedNotification("Successfully created location.");

      // Only one refresh path is needed: a city/state change already triggers its own
      // requery, otherwise the active search needs an explicit retrigger to pick it up.
      // currentFilters.city is stored as a combined "CITY, STATE" string
      const whereFilterCity = `${location.city}, ${location.state}`;
      const isSameWhereFilter = whereFilterCity === currentFilters.city;
      if (isSameWhereFilter) {
        triggerRefetchQuotes();
      } else {
        setPendingWhereFilterUpdate({
          city: location.city,
          state: location.state,
        });
      }
    } catch (err) {
      console.error(err);
      failedNotification("Failed to create location.", (err as any).message);
    }
    setIsSaving(false);
  };

  const addOccupant = () => {
    if (!location) return;
    setLocation({
      ...location,
      occupants: [...location.occupants, createBlankOccupant()],
    });

    const element = document.getElementById("location-panel-body");
    if (element) {
      element.scrollTop = element.scrollHeight + 500;
    }
  };

  useEffect(() => {
    if (
      showAddLocationPanel &&
      location &&
      location.coordinates[0] !== 0 &&
      location.coordinates[1] !== 0
    ) {
      setPinLocation({
        lat: Number(location.coordinates[0]),
        lng: Number(location.coordinates[1]),
      });
    } else {
      // Need to clear pin location when not selectingPinLocation
      setPinLocation(undefined);
    }
  }, [location, setPinLocation, showAddLocationPanel]);

  useEscapeKeyExit({
    active: showAddLocationPanel && !selectingPinLocation,
    close: closeAddLocationPanel,
  });

  return createPortal(
    <>
      {showAddLocationPanel && !selectingPinLocation && (
        <div
          id="focusOnAddLocationPanel"
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: ZIndex.OpaqueBackground,
            backgroundColor: "rgba(15, 44, 82, 0.3)", // Selecting pin location
          }}
        />
      )}

      {showAddLocationPanel && selectingPinLocation && (
        <PinSelectConfirmation
          confirm={() => {
            setMapType("roadmap");
            if (pinLocation && location) {
              setLocation({
                ...location,
                coordinates: [pinLocation.lat, pinLocation.lng],
              });
              setSelectingPinLocation(false);
            }
          }}
          cancel={() => {
            setMapType("roadmap");
            if (location) {
              setPinLocation({
                lat: Number(location.coordinates[0]),
                lng: Number(location.coordinates[1]),
              });
            }
            setSelectingPinLocation(false);
          }}
        />
      )}

      <NitroModal
        id="add-location-panel"
        open={showAddLocationPanel}
        className="addLocationPanel"
        style={{
          zIndex: selectingPinLocation ? -1 : 101,
          width: selectingPinLocation ? 0 : undefined,
        }}
      >
        <div
          style={{
            flexGrow: 1,
            position: "relative",
            width: "100%",
            display: selectingPinLocation ? "none" : "flex",
            flexDirection: "column",
            overflowY: "hidden",
          }}
        >
          {/* HEADER */}
          <div className="locationHeaderContainer">
            <div className="locationHeaderText">Add Location</div>

            <div style={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon onClick={closeAddLocationPanel} />
            </div>
          </div>
          {/* END HEADER */}

          {/* BODY */}
          <div
            id="location-panel-body"
            className="locationBody"
            style={{
              display: isSaving ? "none" : "flex",
            }}
          >
            <div
              className="locationPadding"
              style={{
                color: "#148DD3",
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
              }}
            >
              Facility information
            </div>

            <div
              className="locationPadding"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {/* FORM */}
              <FacilityForm
                location={location || createBlankLocation()}
                setLocation={setLocation}
                startSelectingCoordinates={() => {
                  setSelectingPinLocation(true);
                }}
              />
              {/* END FORM */}

              {/* OCCUPANTS */}
              <Occupants
                occupants={location?.occupants || []}
                setOccupants={(o: IOccupant[]) => {
                  setLocation({
                    ...(location || createBlankLocation()),
                    occupants: o,
                  });
                }}
                addOccupant={addOccupant}
                disabled={occupantsDisabled}
                uom={location?.uom || ""}
              />
              {/* END OCCUPANTS */}

              {/* LOGISTICS */}
              <fieldset
                disabled={logisticsDisabled}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  border: "3px solid #148dd3",
                  borderRadius: 4,
                  opacity: logisticsDisabled ? "50%" : undefined,
                  padding: 0,
                  paddingBottom: 10,
                }}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    height: 43,
                    minHeight: 43,
                    paddingLeft: 26,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      color: "#0076BB",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Logistics
                  </div>
                </div>
                <Logistics
                  location={location || createBlankLocation()}
                  setLocation={setLocation}
                  disabled={logisticsDisabled}
                />
              </fieldset>
              {/* END LOGISTICS */}
            </div>
          </div>
          {/* END BODY */}

          {/* FOOTER */}
          {!isSaving && (
            <div className="locationFooterContainer">
              <Button
                id="saveLocationButton"
                variant="contained"
                fullWidth
                onClick={saveLocation}
                disabled={
                  !isValid ||
                  !occupantsAreValid ||
                  !hasRequiredRailStationAndCarriers(
                    location || createBlankLocation(),
                  )
                }
              >
                SAVE LOCATION
              </Button>
            </div>
          )}
          {/* END FOOTER */}

          <div
            style={{
              flexGrow: 1,
              display: !isSaving ? "none" : "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingIcon color="#005CA6" height={100} width={100} />
          </div>
        </div>
      </NitroModal>
    </>,
    document.body,
  );
};

export default AddLocationPanel;
